<script setup>
  import {inject, ref} from "vue";

  import ComponentCategoryBadge from "@components/Badges/CategoryBadge.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {MedButton} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "PatientinputComponentStep",
  });

  const slots = defineSlots();

  const props = defineProps({
    title: {
      type: String,
      default: null,
    },
    optional: {
      type: Boolean,
      default: false,
    },
    forwardButton: {
      type: Boolean,
      default: true,
    },
    backButton: {
      type: Boolean,
      default: true,
    },
    forwardButtonEnabled: {
      type: Boolean,
      default: true,
    },
    showConfirmDialog: {
      type: Boolean,
      default: false,
    },
    confirmDialogTitle: {
      type: String,
      default: null,
    },
    navigationHorizontal: {
      type: Boolean,
      default: true,
    },
  });

  const navigate = inject("navigate");

  const refConfirmDialog = ref(null);

  const forwardButtonClicked = () => {
    if (props.showConfirmDialog && props.confirmDialogTitle && slots["confirm-dialog-content"]) {
      refConfirmDialog.value.open();
    } else {
      performForward();
    }
  };

  const performBackward = () => {
    navigate("-");
  };

  const performForward = () => {
    navigate("+");
  };
</script>

<template>
  <div class="flex grow flex-col" :data-test="getTestId('step')">
    <section class="mb-6 flex flex-row flex-wrap items-center justify-center gap-1 space-x-2">
      <slot name="title">
        <h1 class="text-center text-3xl md:text-4xl">{{ title }}</h1>

        <div v-if="optional">
          <ComponentCategoryBadge color="info" class="rounded-full! text-lg!">optional</ComponentCategoryBadge>
        </div>
      </slot>
    </section>

    <section class="mb-10 flex flex-col gap-4">
      <slot />
    </section>

    <section :class="cn('flex flex-col gap-3', {'sm:flex-row': navigationHorizontal})">
      <slot name="navigation">
        <template v-if="forwardButton">
          <MedButton
            v-if="forwardButton"
            variant="primary"
            :disabled="!forwardButtonEnabled"
            :data-test="getTestId('step-forward-button')"
            :class="{'flex-1 sm:order-2': navigationHorizontal}"
            @click="forwardButtonClicked"
          >
            Weiter
          </MedButton>
        </template>

        <template v-if="backButton">
          <MedButton
            :class="{'flex-1 sm:order-1': navigationHorizontal}"
            :data-test="getTestId('step-back-button')"
            @click="performBackward"
          >
            Zurück
          </MedButton>
        </template>
      </slot>
    </section>

    <section v-if="$slots['bottom']" class="mt-10">
      <slot name="bottom" />
    </section>

    <ComponentDialog
      v-if="confirmDialogTitle && $slots['confirm-dialog-content']"
      ref="refConfirmDialog"
      primary-label="Weiter"
      :dialog-title="confirmDialogTitle"
      :data-test="getTestId('step-confirm')"
      @primary-button-clicked="performForward"
    >
      <template #content>
        <slot name="confirm-dialog-content" />
      </template>
    </ComponentDialog>
  </div>
</template>
